'use client'

import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAccount } from 'wagmi'
import { CardPanel } from './components/CardPanel'
import { CardReferralShare } from './components/CardReferralShare'
import { CardWaitlist } from './components/CardWaitlist'
import { CardWaitlistJoin } from './components/CardWaitlistJoin'
import { CardEligibilityResult, CardState, KYCStatus } from './types/CardState'
import { attemptAutoResume, hasPersistedCardSessionForAddress } from './utils/sessionResume'
import { CardLandingPage } from './views/CardLandingPage'
import { CardView } from './views/CardView'

type DevOverride = 'live' | 'landing' | 'waitlist' | 'waitlist-join' | 'referral' | CardState

const DEV_STATE_LABELS: Record<CardState, string> = {
  [CardState.CheckEligibility]: 'Eligibility',
  [CardState.NotEligible]: 'Not Eligible',
  [CardState.EligibleWaitlisted]: 'Priority (Waitlisted)',
  [CardState.EligibleRequiresKYC]: 'Priority (Active)',
  [CardState.OnboardingKYC]: 'Onboarding',
  [CardState.KYCPending]: 'KYC Pending',
  [CardState.KYCRejected]: 'KYC Rejected',
  [CardState.KYCIssue]: 'KYC Issue',
  [CardState.AgreementsRequired]: 'Agreements',
  [CardState.AccountSuspended]: 'Suspended',
  [CardState.SessionExpired]: 'Expired',
  [CardState.FullAccess]: 'Full Access',
}

interface CardContainerProps {
  isUSUser: boolean
}

function CardContainer({ isUSUser }: CardContainerProps) {
  const { address } = useAccount()
  const [showLandingPage, setShowLandingPage] = useState(true)
  const [autoResuming, setAutoResuming] = useState(false)
  const [initialEligibilityResult, setInitialEligibilityResult] = useState<CardEligibilityResult | null>(null)
  const [initialSessionExpired, setInitialSessionExpired] = useState(false)
  const [devOverride, setDevOverride] = useState<DevOverride>('live')

  const isDev = process.env.NODE_ENV === 'development'
  const prevAddressRef = useRef(address)

  // Determine if we should show the spinner on mount (avoids landing flash).
  // Also handles address changes (wallet switch, disconnect).
  useEffect(() => {
    const prev = prevAddressRef.current
    prevAddressRef.current = address

    if (!address) {
      setAutoResuming(false)
      if (prev) {
        setShowLandingPage(true)
        setInitialEligibilityResult(null)
        setInitialSessionExpired(false)
      }
      return
    }

    if (hasPersistedCardSessionForAddress(address)) {
      setInitialEligibilityResult(null)
      setInitialSessionExpired(false)
      setAutoResuming(true)
    } else {
      setAutoResuming(false)
      if (prev !== address || !prev) {
        setShowLandingPage(true)
        setInitialEligibilityResult(null)
        setInitialSessionExpired(false)
      }
    }
  }, [address])

  // Auto-resume: only runs when address is known and has a resumable server session
  useEffect(() => {
    if (!autoResuming || !address) return

    let cancelled = false

    const run = (retries: number): Promise<void> =>
      attemptAutoResume(address)
        .then((result) => {
          if (cancelled) return
          setInitialEligibilityResult(result)
          setShowLandingPage(false)
        })
        .catch((err) => {
          if (cancelled) return
          if (retries > 0) return run(retries - 1)

          const isSessionError = err?.message?.includes('Authentication failed') || err?.message?.includes('No valid session')
          if (isSessionError) {
            setInitialSessionExpired(true)
            setShowLandingPage(false)
          }
        })

    run(1).finally(() => {
      if (cancelled) return
      setAutoResuming(false)
    })

    return () => {
      cancelled = true
    }
  }, [autoResuming, address])

  const handleEligibilityChecked = useCallback((result: CardEligibilityResult) => {
    setInitialEligibilityResult(result)
    setShowLandingPage(false)
  }, [])

  const isDevOverrideActive = isDev && devOverride !== 'live'
  const showLanding = isDevOverrideActive ? devOverride === 'landing' : showLandingPage
  const devCardState = isDevOverrideActive && devOverride !== 'landing' ? (devOverride as CardState) : null

  const devMockEligibility: CardEligibilityResult | null = (() => {
    if (!devCardState) return null
    const mockWaitlist = {
      walletAddress: '0x0000',
      tenantID: 'dev',
      position: 42,
      referralCode: 'DEV123',
      referralCount: 3,
      boostScore: 150,
      status: 'waitlisted',
      joinedAt: new Date().toISOString(),
    }
    switch (devCardState) {
      case CardState.NotEligible:
        return { isEligible: false, isRequiredKYC: true, kycStatus: KYCStatus.APPROVED, waitlistEntry: mockWaitlist, accountStatus: 'waitlisted' as const }
      case CardState.EligibleWaitlisted:
        return { isEligible: true, isRequiredKYC: true, kycStatus: KYCStatus.APPROVED, waitlistEntry: mockWaitlist, autoOnboard: false, accountStatus: 'waitlisted' as const }
      case CardState.EligibleRequiresKYC:
        return { isEligible: true, isRequiredKYC: true, kycStatus: KYCStatus.NOT_REQUESTED, accountStatus: 'active' as const }
      case CardState.KYCPending:
        return { isEligible: true, isRequiredKYC: true, kycStatus: KYCStatus.PENDING, accountStatus: 'active' as const }
      case CardState.AccountSuspended:
        return { isEligible: false, isRequiredKYC: false, kycStatus: KYCStatus.APPROVED, accountStatus: 'frozen' as const }
      default:
        return null
    }
  })()

  const devToolbar = isDev ? (
    <div className="fixed bottom-4 left-4 z-50 flex max-w-[calc(100vw-2rem)] flex-wrap items-center gap-1 rounded-lg border border-border bg-background/95 p-1.5 shadow-lg backdrop-blur-sm">
      <Typography variant="p" className="px-2 text-muted-foreground text-xs">
        State
      </Typography>
      <button
        onClick={() => setDevOverride('live')}
        className={cn(
          'rounded px-2 py-1 text-xs transition-colors',
          devOverride === 'live' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
        )}
      >
        Live
      </button>
      <button
        onClick={() => setDevOverride('landing')}
        className={cn(
          'rounded px-2 py-1 text-xs transition-colors',
          devOverride === 'landing' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
        )}
      >
        Landing
      </button>
      <div className="mx-0.5 h-4 w-px bg-border" />
      {Object.values(CardState)
        .filter((state) => state !== CardState.CheckEligibility)
        .map((state) => (
          <button
            key={state}
            onClick={() => setDevOverride(state)}
            className={cn(
              'rounded px-2 py-1 text-xs transition-colors',
              devOverride === state ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
            )}
          >
            {DEV_STATE_LABELS[state]}
          </button>
        ))}
      <div className="mx-0.5 h-4 w-px bg-border" />
      {(['waitlist', 'waitlist-join', 'referral'] as const).map((view) => (
        <button
          key={view}
          onClick={() => setDevOverride(view)}
          className={cn(
            'rounded px-2 py-1 text-xs transition-colors',
            devOverride === view ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {view === 'waitlist' ? 'Waitlist' : view === 'waitlist-join' ? 'Join WL' : 'Referral'}
        </button>
      ))}
    </div>
  ) : null

  if (isDev && devOverride === 'waitlist') {
    return (
      <>
        <CardPanel>
          <CardWaitlist />
        </CardPanel>
        {devToolbar}
      </>
    )
  }

  if (isDev && devOverride === 'waitlist-join') {
    return (
      <>
        <CardPanel>
          <CardWaitlistJoin />
        </CardPanel>
        {devToolbar}
      </>
    )
  }

  if (isDev && devOverride === 'referral') {
    return (
      <>
        <CardPanel>
          <div className="flex justify-center p-8">
            <div className="w-full max-w-xl">
              <CardReferralShare referralCode="DEMO-CODE" />
            </div>
          </div>
        </CardPanel>
        {devToolbar}
      </>
    )
  }

  if (autoResuming) {
    return (
      <>
        <CardPanel>
          <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <Typography variant="p" className="text-muted-foreground">
              Resuming your session...
            </Typography>
          </div>
        </CardPanel>
        {devToolbar}
      </>
    )
  }

  if (showLanding) {
    return (
      <>
        <CardLandingPage onEligibilityChecked={handleEligibilityChecked} />
        {devToolbar}
      </>
    )
  }

  return (
    <>
      <CardPanel>
        <CardView
          key={address}
          initialEligibilityResult={devMockEligibility ?? initialEligibilityResult}
          initialSessionExpired={initialSessionExpired}
          devOverrideState={devCardState}
          isUSUser={isUSUser}
        />
      </CardPanel>
      {devToolbar}
    </>
  )
}

export { CardContainer }
